import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

import { GhostIcon, MailIcon, UserIcon } from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { MSTeamsIcon } from "simplydo/react/ui/brand-icon";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import BaseChip, { type ChipConfiguration } from "./BaseChip";
import { SocialLinks } from "./SocialLinks";
import type { ChipUser } from "./types";

type IUserChipBase = { user: ChipUser | undefined; showSocials?: boolean };

type IUserChip = IUserChipBase & ChipConfiguration;

function UserChip({
  user: userProp,
  actionIcon,
  actionClick,
  actionTooltip,
  showSocials,
  ...props
}: IUserChip) {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const currentUser = useAppSelector((state) => state.user);

  const params = useParams();
  const matchId = params?.id;

  let user: ChipUser =
    userProp ?? ({ profile: { fullName: "Unknown User" } } as ChipUser);
  const hasProfile = user?.profile;
  if (!hasProfile) {
    user = { profile: { fullName: "Unknown User" } } as ChipUser;
  }

  const { jobTitle = "", department = "", tagline = "" } = user.profile ?? {};
  let description = jobTitle;
  if (department) {
    if (jobTitle) {
      description += " - ";
    }
    description += department;
  }
  if (!description && tagline) {
    description = tagline;
  }

  const permissionUser: UserMe | undefined = currentUser ?? undefined;

  const userCanListUsers =
    util.hasPermission(
      permissionUser,
      "org.listUsers",
      currentUser?.ownerOrganisation?._id,
    ) || util.hasPermission(permissionUser, "group.listUsers", matchId);

  const userCanViewEmails = util.hasPermission(
    permissionUser,
    "org.viewUserEmails",
    currentUser?.ownerOrganisation?._id,
  );

  const messageUser = (member: ChipUser) => {
    api.messages.findThreadByUser(
      member._id,
      (data) => {
        const rawThread = data?.thread;
        const existingThread = Array.isArray(rawThread)
          ? rawThread[0]
          : rawThread;
        dispatch(
          actions.messages.selectThread(
            existingThread || {
              _id: `newThread:${member._id}`,
              participantUsers: [member],
            },
          ),
        );
      },
      (err) => toast.error(err.message),
    );
  };

  const canMessage =
    user._id !== currentUser?._id &&
    currentUser?.ownerOrganisation?.enabledFeatures?.includes(
      "directMessaging",
    );
  const teamsHandle = user?.teamsHandle;
  const canMessageViaTeams =
    user._id !== currentUser?._id &&
    !!currentUser?.ownerOrganisation?.enabledFeatures?.includes(
      "teamsHandleAdvertising",
    ) &&
    !!teamsHandle;

  const messageUserViaTeams = () => {
    if (!teamsHandle) return;
    const teamsUrl = `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(teamsHandle)}`;
    window.open(teamsUrl, "_blank", "noopener,noreferrer");
  };

  const ghost = () => {
    util
      .confirm(
        "Really ghost as this user?",
        "Ghosting allows you to view the platform and carry out actions as though you are the user. You can un-ghost at any time by tapping the avatar at the top-right of the screen.",
      )
      .then(() =>
        api.users.ghost(
          user?._id,
          () => {
            api.queryClient.invalidateQueries();
            dispatch(actions.user.ghost(user?._id));
            navigate("/");
          },
          (err) => toast.error(err.message),
        ),
      )
      .catch(() => {});
  };

  const canGhost =
    user?._id &&
    !(user?._id === currentUser?._id) &&
    util.hasPermission(
      permissionUser,
      "super.ghostUser",
      currentUser?.ownerOrganisation?._id,
    );

  const hasFooterButtons =
    canMessage || canMessageViaTeams || userCanListUsers || canGhost;

  const customMappings =
    currentUser?.ownerOrganisation?.auth?.saml?.customMappings ?? [];

  return (
    <BaseChip
      {...props}
      title={
        <>
          {user.profile?.fullName}
          {showSocials ? <SocialLinks user={user} /> : null}
        </>
      }
      largeTitle={
        <>
          {user.profile?.fullName}
          <SocialLinks user={user} />
        </>
      }
      subtitle={description}
      content={
        <>
          {userCanViewEmails && user?.emails ? (
            <p
              style={{
                marginTop: 0,
                marginBottom: 5,
                fontSize: 13,
                color: "grey",
              }}
            >
              <Icon icon={MailIcon} style={{ marginRight: 5 }} />
              {user.emails?.[0]?.address}
            </p>
          ) : null}
          {description !== tagline ? (
            <p
              style={{
                color: "grey",
                fontSize: 13,
                marginBottom: 8,
                marginTop: 0,
              }}
            >
              {tagline}
            </p>
          ) : null}
          {currentUser?.ownerOrganisation?.auth?.saml?.customMappings &&
          user?.profile?.attributes &&
          Object.keys(user.profile.attributes).length > 0 ? (
            <div style={{ marginBottom: 10 }}>
              {customMappings.map(
                (mapping: { localKey: string; displayName: string }) =>
                  user?.profile?.attributes?.[mapping.localKey] && (
                    <div key={mapping.localKey} style={{ marginBottom: 5 }}>
                      {mapping.displayName}:{" "}
                      {user?.profile?.attributes[mapping.localKey]}
                    </div>
                  ),
              )}
            </div>
          ) : null}
        </>
      }
      disabled={!hasProfile}
      imageUrl={util.avatarUrl(user, "200x200")}
      labels={user.iconRoles}
      actionIcon={actionIcon}
      actionClick={actionClick}
      actionTooltip={actionClick ? actionTooltip : undefined}
      footer={
        hasFooterButtons ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              justifyContent: "flex-end",
              paddingLeft: "25%",
            }}
          >
            {canMessage ? (
              <Button
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  messageUser(user);
                }}
              >
                <Icon icon={MailIcon} />
                Message
              </Button>
            ) : null}
            {canMessageViaTeams ? (
              <Button
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  messageUserViaTeams();
                }}
              >
                <Icon icon={MSTeamsIcon} size="small" />
                Message via Teams
              </Button>
            ) : null}
            {userCanListUsers ? (
              <Button
                size="small"
                onClick={() => navigate(`/users/${user?._id}`)}
              >
                <Icon icon={UserIcon} />
                View profile
              </Button>
            ) : null}
            {canGhost ? (
              <Button
                size="small"
                onClick={() => {
                  document.dispatchEvent(
                    new MouseEvent("click", { bubbles: true }),
                  );
                  ghost();
                }}
              >
                <Icon icon={GhostIcon} />
                Ghost
              </Button>
            ) : null}
          </div>
        ) : null
      }
    />
  );
}

export default UserChip;
